import { z } from 'zod';

// ---------------------------------------------------------------------------
// Dashboard Parental (area D dos requisitos): o uso da Arena de um jovem, como
// o encarregado o ve. A arena-api calcula-o (`/internal/youths/:id/activity`);
// a members-api confirma que quem pede e encarregado e devolve-o tal e qual em
// `GET /api/members/guardian/youths/:id/activity`.
//
// So presenca: arena, posto, entrada e saida. Nunca que jogos (RN-A14).
// ---------------------------------------------------------------------------

const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'data no formato AAAA-MM-DD');

/** O periodo mais longo que se pede de uma vez (um ano e um dia, por causa dos bissextos). */
export const ACTIVITY_MAX_DAYS = 366;

export const activitySessionSchema = z.object({
  id: z.string().uuid(),
  arenaName: z.string(),
  workstationName: z.string(),
  startedAt: z.string().datetime(),
  /** `null` enquanto o jovem ainda esta no PC. */
  endedAt: z.string().datetime().nullable(),
  minutes: z.number(),
  /** Entrou durante a pausa obrigatoria (RN-B9): o PC avisou e deixou. */
  duringBreak: z.boolean(),
});
export type ActivitySession = z.infer<typeof activitySessionSchema>;

/** Um dia (em Lisboa) em que o jovem jogou. Os dias sem uso nao aparecem. */
export const activityDaySchema = z.object({
  date: localDate,
  minutes: z.number(),
  sessions: z.number().int(),
  /** Limite diario desse dia, ja com os extras (associado, merito, tempo dado pelo monitor). */
  dailyLimitMinutes: z.number(),
  /** A maior sequencia seguida desse dia, com as pausas curtas la dentro (como no PC). */
  longestRunMinutes: z.number(),
  overDaily: z.boolean(),
  overContinuous: z.boolean(),
});
export type ActivityDay = z.infer<typeof activityDaySchema>;

const activityTotalSchema = z.object({
  minutes: z.number(),
  /** Dias com uso. */
  days: z.number().int(),
  /** Dias com algum limite ultrapassado. */
  overLimitDays: z.number().int(),
});

export const youthActivitySchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  /** Datas de Lisboa, inclusive. */
  from: localDate,
  to: localDate,
  /** Os limites da arena do jovem, sem extras: o que o resumo mostra como regra. */
  policy: z.object({
    maxContinuousMinutes: z.number().int(),
    breakMinutes: z.number().int(),
    maxDailyMinutes: z.number().int(),
  }),
  sessions: z.array(activitySessionSchema),
  days: z.array(activityDaySchema),
  /** Semanas de segunda a domingo; `start` e a segunda. So as que tocam o periodo. */
  weeks: z.array(activityTotalSchema.extend({ start: localDate })),
  /** `month` e `AAAA-MM`. So os que tocam o periodo. */
  months: z.array(activityTotalSchema.extend({ month: z.string().regex(/^\d{4}-\d{2}$/) })),
  total: activityTotalSchema,
});
export type YouthActivity = z.infer<typeof youthActivitySchema>;

/** `?from=AAAA-MM-DD&to=AAAA-MM-DD` dos pedidos de atividade. */
export const activityRangeSchema = z
  .object({ from: localDate, to: localDate })
  .refine((r) => r.from <= r.to, 'from depois de to')
  .refine(
    (r) =>
      (Date.parse(`${r.to}T00:00:00Z`) - Date.parse(`${r.from}T00:00:00Z`)) / 86_400_000 <
      ACTIVITY_MAX_DAYS,
    `no maximo ${String(ACTIVITY_MAX_DAYS)} dias`,
  );
export type ActivityRange = z.infer<typeof activityRangeSchema>;

/** `PUT /guardian/youths/:id/weekly-email`: ligar ou desligar o resumo semanal (RF-D4). */
export const weeklyEmailRequestSchema = z.object({ enabled: z.boolean() });
